import React, { useRef, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, FeatureGroup } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import ErrorBoundary from './ErrorBoundary';

// Loosen types locally to avoid missing @types/leaflet issues
const AnyMapContainer = MapContainer as any;
const AnyTileLayer = TileLayer as any;
const AnyFeatureGroup = FeatureGroup as any;
const AnyEditControl = EditControl as any;

// Fix for default Leaflet icon issues with Webpack/Vite
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

interface DeliveryZoneMapProps {
  initialGeoJSON?: GeoJSON.FeatureCollection; // Optional initial polygons to display
  onDrawComplete: (geoJSON: GeoJSON.FeatureCollection) => void; // Callback when drawing is complete
}

const DeliveryZoneMap: React.FC<DeliveryZoneMapProps> = ({ initialGeoJSON, onDrawComplete }) => {
  const featureGroupRef = useRef<any>(null);

  const onCreated = useCallback((e: any) => {
    const { layer } = e;
    // Ensure it's a polygon
    if (layer instanceof L.Polygon) {
      const geoJson = layer.toGeoJSON();
      // Wrap the single feature in a FeatureCollection
      onDrawComplete({
        type: 'FeatureCollection',
        features: [geoJson]
      });
    }
  }, [onDrawComplete]);

  const onEdited = useCallback((e: any) => {
    const { layers } = e;
    const features: GeoJSON.Feature[] = [];
    layers.eachLayer((layer: any) => {
      if (layer instanceof L.Polygon) {
        features.push(layer.toGeoJSON());
      }
    });
    onDrawComplete({
      type: 'FeatureCollection',
      features: features
    });
  }, [onDrawComplete]);

  const onDeleted = useCallback(() => {
    // When a layer is deleted, the feature group will be empty
    onDrawComplete({
      type: 'FeatureCollection',
      features: []
    });
  }, [onDrawComplete]);

  useEffect(() => {
    if (featureGroupRef.current && initialGeoJSON) {
      // Clear existing layers
      featureGroupRef.current.clearLayers();
      // Add initial GeoJSON layers
      L.geoJSON(initialGeoJSON, {
        onEachFeature: (_feature: any, layer: any) => {
          if (layer instanceof L.Polygon) {
            featureGroupRef.current?.addLayer(layer);
          }
        }
      });
    }
  }, [initialGeoJSON]);

  // Inject small CSS overrides to wrap Leaflet Draw toolbar and allow scrolling when needed
  useEffect(() => {
    const id = 'delivery-zone-map-wrap-styles';
    if (!document.getElementById(id)) {
      const style = document.createElement('style');
      style.id = id;
      style.innerHTML = `
/* DeliveryZoneMap wrap and overflow fixes */
.delivery-zone-map .leaflet-top.leaflet-right {
  max-width: 100%;
  overflow-x: auto;
  padding-bottom: 4px;
}
.delivery-zone-map .leaflet-draw-toolbar {
  display: flex !important;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 100%;
}
.delivery-zone-map .leaflet-bar a,
.delivery-zone-map .leaflet-bar a:hover {
  display: inline-flex;
  white-space: normal;
}
`;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className="max-w-full overflow-x-auto delivery-zone-map-container-wrapper">
      <ErrorBoundary fallback={<div className="p-4 bg-red-50 text-red-500 rounded border border-red-200">Failed to load map. See console for details.</div>}>
        <AnyMapContainer
          className="delivery-zone-map w-full"
          center={[40.416775, -3.703790]} // Center of Spain (Madrid)
          zoom={5}
          style={{ height: '500px', width: '100%' }}
          scrollWheelZoom={true}
        >
          <AnyTileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AnyFeatureGroup ref={featureGroupRef}>
            <AnyEditControl
              position="topright"
              onCreated={onCreated}
              onEdited={onEdited}
              onDeleted={onDeleted}
              draw={{
                rectangle: false,
                circle: false,
                marker: false,
                polyline: false,
                circlemarker: false,
                polygon: {},
              }}
            />
          </AnyFeatureGroup>
        </AnyMapContainer>
      </ErrorBoundary>
    </div>
  );
};

export default DeliveryZoneMap;